/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useImportJobFlow } from '@/hooks/useImportJobFlow';
import { ROUTES } from '@/types';

export type ImportSheetInitialView = 'default' | 'url' | 'image' | 'text' | 'collection';

interface ImportSheetContextValue {
  isOpen: boolean;
  initialView: ImportSheetInitialView;
  open: (view?: ImportSheetInitialView) => void;
  close: () => void;
  // Import job flow — lives here so polling survives the drawer closing
  startPolling: (jobId: string) => void;
  isPolling: boolean;
  closeExistingToast: () => void;
}

export const ImportSheetContext = createContext<ImportSheetContextValue | null>(null);

export function ImportSheetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialView, setInitialView] = useState<ImportSheetInitialView>('default');
  const [, setLocation] = useLocation();

  const { isPolling, startPolling, closeExistingToast } = useImportJobFlow({
    onSuccess: (recipeId) => {
      setIsOpen(false);
      setInitialView('default');
      setLocation(ROUTES.RECIPE(recipeId));
    },
  });

  return (
    <ImportSheetContext.Provider
      value={{
        isOpen,
        initialView,
        open: (view = 'default') => { setInitialView(view); setIsOpen(true); },
        close: () => { setIsOpen(false); setInitialView('default'); },
        startPolling,
        isPolling,
        closeExistingToast,
      }}
    >
      {children}
    </ImportSheetContext.Provider>
  );
}

export function useImportSheet(): ImportSheetContextValue {
  const ctx = useContext(ImportSheetContext);
  if (!ctx) throw new Error('useImportSheet must be used within ImportSheetProvider');
  return ctx;
}
